import React, { memo } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { useNavigate, useParams } from 'react-router-dom'

import { PageLayoutContainer } from '../../../lib/page-layout-container'
import { isAdminDomain } from '../../../lib/currentDomain'
import { LoadBalancerStatistics } from '../../../lib/load-balancer-statistics'
import { MeasurementDeviceFormWidget } from '../../features/measurement-device/measurement-device-form-widget'
import { MeasurementDeviceDetailsWidget } from '../../features/measurement-device/measurement-device-details-widget'
import { MeasurementDevicePropertiesWidget } from '../../features/measurement-device/measurement-device-properties-widget'
import {
    useMeasurementDevicesGetByIdQuery,
    useMeasurementDevicesGetLatestMeasurementQuery,
    useMeasurementDevicesGetMeasurementsForLatest24HoursQuery,
    useLazyLoadBalancerGroupGetSnapshotByDeviceIdQuery,
    useLoadBalancerGroupGetByIdQuery,
} from '../../redux/api/web-api'
import { MeasurementDeviceStatistics } from '../../components/measurement-device-statistics'
import { Button } from '../../components/Button'
import MeasurementDeviceStatusPanel from '../../components/MeasurementDeviceStatusPanel'
import useText from '../../hooks/useText'

const SActions = styled.div`
    display: flex;
    justify-content: flex-end;
    padding-bottom: 20px;
    gap: 4px;
`

const SWidgetsGroups = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
`

const SWidgetsGroup = styled.div`
    width: 100%;

    @media (min-width: 990px) {
        width: 49%;
    }
`

export type MeasurementDevicePageProps = {
    className?: ClassName
    style?: React.CSSProperties
}

export const MeasurementDevicePage = memo(function MeasurementDevicePage({
    className,
    style,
}: MeasurementDevicePageProps): React.ReactElement {
    const text = useText()

    const { deviceId } = useParams()
    const navigate = useNavigate()

    const {
        data: device,
        refetch: getMeasurementDevice,
        isFetching: measurementDeviceIsFetching,
    } = useMeasurementDevicesGetByIdQuery(
        {
            idOrSerialNumber: deviceId?.toString() ?? '',
        },
        {
            skip: !deviceId,
        }
    )

    const {
        data: latestMeasurement,
        refetch: getMeasurementData,
        isFetching: latestMeasurementIsFetching,
    } = useMeasurementDevicesGetLatestMeasurementQuery(
        {
            id: Number(deviceId),
        },
        {
            skip: !deviceId,
        }
    )

    const { data: loadBalancerData } = useLoadBalancerGroupGetByIdQuery(
        {
            id: device?.loadBalancerGroupId ?? 0,
        },
        {
            skip: !device?.loadBalancerGroupId,
        }
    )

    const [triggerPrefetch, { isFetching: snapshotDataIsFetching }] =
        useLazyLoadBalancerGroupGetSnapshotByDeviceIdQuery()

    const {
        refetch: getMeasurementDeviceGetMeasurementsForLatest24Hours,
        isFetching: measurementDeviceMeasurementsAreFetching,
    } = useMeasurementDevicesGetMeasurementsForLatest24HoursQuery(
        {
            id: device?.id ?? 0,
        },
        {
            skip: loadBalancerData?.isOcppLoadBalancer,
        }
    )

    const refetchDeviceGraph = (id: number) => {
        triggerPrefetch({ measurementDeviceId: id })
    }
    const onRefresh = () => {
        getMeasurementDevice()
        getMeasurementData()
        refetchDeviceGraph(device?.id)
        if (device?.loadBalancerGroupId && !loadBalancerData?.isOcppLoadBalancer) {
            getMeasurementDeviceGetMeasurementsForLatest24Hours()
        }
    }

    const isFetching =
        measurementDeviceIsFetching ||
        latestMeasurementIsFetching ||
        snapshotDataIsFetching ||
        measurementDeviceMeasurementsAreFetching

    return (
        <PageLayoutContainer className={cn(className)} style={style}>
            <SActions>
                <Button
                    size="md"
                    label={text('btnRefresh')}
                    onClick={() => onRefresh()}
                    data-testid="btn-refresh"
                    disabled={isFetching}
                    loading={isFetching}
                />
                <Button
                    size="md"
                    label={text('btnBack')}
                    onClick={() => navigate('/measurementDevices')}
                    data-testid="btn-back"
                />
            </SActions>
            <SWidgetsGroups>
                <SWidgetsGroup>
                    {isAdminDomain ? (
                        <MeasurementDeviceDetailsWidget device={device} />
                    ) : (
                        device && <MeasurementDeviceFormWidget device={device} />
                    )}
                </SWidgetsGroup>
                <SWidgetsGroup>
                    <MeasurementDevicePropertiesWidget device={device} />
                    <MeasurementDeviceStatusPanel
                        device={device}
                        latestMeasurement={latestMeasurement}
                        loadBalancerData={loadBalancerData}
                    />
                </SWidgetsGroup>
            </SWidgetsGroups>

            <LoadBalancerStatistics device={device} graphTitle={text('lblChargingAndMeasured')} />

            {/* check if Lb has capi chargers */}
            {device?.loadBalancerGroupId && !loadBalancerData?.isOcppLoadBalancer ? (
                <MeasurementDeviceStatistics device={device} />
            ) : null}
        </PageLayoutContainer>
    )
})
